/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { useState, useEffect, useCallback, SetStateAction, Dispatch } from 'react';

// A more robust implementation of useLocalStorage that prevents race conditions
// and syncs state across tabs.
function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  // State to store our value
  /* 
    Phase 1: Observability & Debug
    Added ?debug=1 support to enable verbose logging in localStorage 'JENIAL_DEBUG' 
  */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('debug') === '1') {
        console.log('[Debug] Debug mode enabled via URL parameter');
        window.localStorage.setItem('JENIAL_DEBUG', '1');
      }
    }
  }, []);

  // Pass a lazy initializer function to useState so this logic is only executed once.
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      if (!item) return initialValue;

      // [FIX] Handle non-JSON values that might have been stored by legacy code
      // If parsing fails, we catch it below.
      return JSON.parse(item) as T;
    } catch (error) {
      if (typeof window !== 'undefined' && window.localStorage.getItem('JENIAL_DEBUG')) {
        console.warn(`[LS][CORRUPT_KEY][${key}] Removing corrupted key. Error:`, error);
      }
      // CORRUPTION FIX: Remove bad key so we start fresh next time
      try { window.localStorage.removeItem(key); } catch { }
      return initialValue;
    }
  });

  // useCallback ensures the function reference is stable
  const setValue = useCallback(
    (value: SetStateAction<T>) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        // Save state
        setStoredValue(valueToStore);
        // Save to local storage
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.warn(`Error setting localStorage key “${key}”:`, error);
      }
    },
    [key, storedValue],
  );

  // Listen for changes to the same key from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue) {
        try {
          setStoredValue(JSON.parse(event.newValue) as T);
        } catch (error) {
          console.warn(
            `Error parsing storage change for key “${key}”:`,
            error,
          );
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key]);

  return [storedValue, setValue];
}

export default useLocalStorage;
